import { HttpClient, HttpHeaders } from '@angular/common/http';
import { EventEmitter, Injectable, Injector, Output } from '@angular/core';
import { jwtDecode } from 'jwt-decode';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { User } from '../models/user';
import { ControllerApiPath } from './constant';

@Injectable({
  providedIn: 'root',
})
export class SharedServiceService {
  private apiBaseUrl = environment.apiBaseUrl;
  private apiBaseUrlOld = environment.apiBaseUrlOld;
  private httpClient: HttpClient;
  userRole: any = [];
  userDetails: any = [];

  @Output() getLoggedIn: EventEmitter<boolean> = new EventEmitter(false);

  // Measurement options restricted to ea, oz, fl oz, pkg
  // Index mapping is preserved because other parts of the app expect this structure.
  allMeasurement: any[] = [
    // 0 - Egg-like items: pieces
    ['ea'],
    // 1 - Liquids
    ['fl oz', 'pkg'],
    // 2 - Semi-liquids
    ['fl oz', 'pkg'],
    // 3 - Solids
    ['oz', 'ea', 'pkg'],
    // 4 - Semi-solids
    ['oz', 'ea', 'pkg'],
    // 5 - Oils
    ['fl oz', 'pkg'],
    // 6 - Meats
    ['oz', 'ea', 'pkg'],
    // 7 - Egg piece weights (approximate grams per ea)
    { ea: 50 },
    // 8 - Liquid measures (approximate mL per unit)
    { 'fl oz': 30, pkg: 240 },
    // 9 - Semi-liquid measures (approximate mL per unit)
    { 'fl oz': 30, pkg: 240 },
    // 10 - Solid measures (approximate grams per unit)
    { oz: 28, ea: 50, pkg: 454 },
    // 11 - Semi-solid measures (approximate grams per unit)
    { oz: 28, ea: 50, pkg: 454 },
    // 12 - Oil measures (approximate mL per unit)
    { 'fl oz': 30, pkg: 240 },
    // 13 - Meat measures (approximate grams per unit)
    { oz: 28, ea: 85, pkg: 454 },
  ];

  dietTags: string[] = ['Vegetarian', 'Vegan', 'Keto', 'Paleo', 'Low-Carb'];
  allergyTags: string[] = ['Gluten', 'Dairy', 'Nuts', 'Shellfish', 'Soy'];

  constructor(private injector: Injector) {
    // Lazy-load HttpClient here to avoid circular DI issues
    setTimeout(() => {
      this.httpClient = this.injector.get(HttpClient);
    });
  }

  private get http(): HttpClient {
    if (!this.httpClient) {
      this.httpClient = this.injector.get(HttpClient);
    }
    return this.httpClient;
  }

  getToken() {
    const token = localStorage.getItem('token') ?? '';
    return token?.replace(/['"]+/g, '');
  }

  getTags(): Observable<any> {
    const headers = this.createAuthorizationHeader();
    const url = `${this.apiBaseUrl.replace(
      /\/+$/,
      ''
    )}/dhruvaapi/Recipe/getTags`;

    return this.http.get<any[]>(url, { headers }).pipe(
      map((tags) => {
        const dietTags = tags
          .filter((tag) => tag.isDiet)
          .map((tag) => ({ tagName: tag.name }));
        const allergyTags = tags
          .filter((tag) => !tag.isDiet)
          .map((tag) => ({ tagName: tag.name }));
        return { dietTags, allergyTags };
      })
    );
  }

  createTag(payload: { name: string; isDiet: boolean }): Observable<any> {
    const headers = this.createAuthorizationHeader();
    const url = `${this.apiBaseUrl.replace(
      /\/+$/,
      ''
    )}/dhruvaapi/Recipe/createTag`;

    return this.http.post(url, payload, { headers });
  }

  createAuthorizationHeader() {
    const token = this.getToken();
    return new HttpHeaders({
      Accept: 'application/json',
      Authorization: `Bearer ${token}`,
      'X-ApiAccess-Key': environment.apiKey,
    });
  }

  createAuthorizationHeaderForRestaurant() {
    return new HttpHeaders({
      Accept: 'application/json',
      'X-Api-Key': environment.apiKeyForRestaurant,
    });
  }

  createAuthorizatioChatAPInHeader() {
    return new HttpHeaders({
      Accept: 'application/json',
      'x-api-key': environment.apiAccessKeyForChatapi,
    });
  }

  baseUrl() {
    return environment.apiBaseUrl;
  }

  getapiBaseUrlOld() {
    return this.apiBaseUrlOld;
  }

  getloggedUserRole() {
    const decodedToken = jwtDecode<any>(localStorage.getItem('token') ?? '');
    return decodedToken.role;
  }

  getUserById(usrId: string): Observable<any> {
    const headers = this.createAuthorizationHeader();
    const url =
      this.apiBaseUrl + ControllerApiPath.getUserById + '?userId=' + usrId;
    return this.http.get(url, { headers });
  }

  checkLogin(): Observable<boolean> {
    const localStorageToken = localStorage.getItem('token');
    const loggedIn = localStorageToken !== null && localStorageToken !== '';

    if (loggedIn) {
      const user = this.getLoggedInUser();
      const cookiesToken = this.getCookie('redirect-auth-token');

      if (cookiesToken) localStorage.setItem('token', cookiesToken);

      const decodedToken = jwtDecode<any>(localStorage.getItem('token') ?? '');
      const userId = decodedToken.unique_name;

      if (user && user.id === userId) return of(true);

      return this.getUserById(userId).pipe(
        map((response) => {
          if (response?.result) {
            this.userDetails = response.result;
            this.setCurrentUser(response.result);
            return true;
          } else {
            return false;
          }
        })
      );
    }

    const cookiesToken = this.getCookie('redirect-auth-token');
    if (!cookiesToken) return of(false);

    const decodedToken = jwtDecode<any>(cookiesToken);
    const userId = decodedToken.unique_name;
    localStorage.setItem('token', cookiesToken);

    return this.getUserById(userId).pipe(
      map((response) => {
        if (response?.result) {
          this.userDetails = response.result;
          this.setCurrentUser(response.result);
          return true;
        } else {
          return false;
        }
      })
    );
  }

  setCurrentUser(userDetails: User) {
    localStorage.setItem('dhruvaUser', JSON.stringify(userDetails));
  }

  logout() {
    localStorage.removeItem('dhruvaUser');
    localStorage.removeItem('token');
    localStorage.removeItem('auth-token');
    localStorage.removeItem('wholeData');
    localStorage.removeItem('selectedType');
    localStorage.removeItem('startDate');
    localStorage.removeItem('endDate');
    localStorage.removeItem('foodData');
    sessionStorage.clear();
    this.delete_cookie('redirect-auth-token', '', -5, '/');
    this.delete_cookie('redirect-auth-token-new', '', -5, '/');
    localStorage.clear();
    this.getLoggedIn.emit(true);
  }

  delete_cookie(
    name: string,
    value: string,
    expireDays: number,
    path: string = ''
  ) {
    const d = new Date();
    d.setTime(d.getTime() + expireDays * 24 * 60 * 60 * 1000);
    const expires = `expires=${d.toUTCString()}`;
    const cpath = path ? `; path=${path}` : '';
    document.cookie = `${name}=${value}; ${expires}${cpath};Domain=.completumhealth.com;SameSite=Lax;Secure;`;
  }

  getLoggedInUser() {
    this.userDetails = localStorage.getItem('dhruvaUser');
    this.userDetails = JSON.parse(this.userDetails);
    return this.userDetails;
  }

  getCookie(name: string): string {
    const nameEQ = name + '=';
    const cookies = document.cookie.split(';');
    for (let cookie of cookies) {
      cookie = cookie.trim();
      if (cookie.startsWith(nameEQ)) {
        return decodeURIComponent(cookie.substring(nameEQ.length));
      }
    }
    return '';
  }
  searchMembers(param: any, searchType: any = 2): Observable<any> {
    this.getLoggedInUser();

    const isAdmin =
      this.userDetails?.userRoles?.[0]?.userRole === 'Administrator';
    const loggedUser = isAdmin ? '' : this.userDetails?.id;
    const searchTypeVal = searchType == 1;

    const headers = this.createAuthorizationHeader();
    const url = `${this.apiBaseUrl}${ControllerApiPath.members}/searchMembers?name=${param}&isAdmin=${isAdmin}&userLoggedIn=${loggedUser}&allUnassigned=${searchTypeVal}`;

    return this.http.get(url, { headers });
  }

  searchMemberss(param: any, teamid: any): Observable<any> {
    this.getLoggedInUser();

    const isAdmin =
      this.userDetails?.userRoles?.[0]?.userRole === 'Administrator';
    const loggedUser = isAdmin ? '' : this.userDetails?.id;

    const headers = this.createAuthorizationHeader();
    const url = `${this.apiBaseUrl}${ControllerApiPath.members}/searchMembersbyid?name=${param}&teamid=${teamid}&isAdmin=${isAdmin}&userLoggedIn=${loggedUser}`;

    return this.http.get(url, { headers });
  }

  getAllMeasure() {
    return this.allMeasurement;
  }
}
